import { FiSearch, FiDownload, FiFileText, FiCalendar } from "react-icons/fi";
import { useEffect, useState, FormEvent, ReactNode } from "react";
import reports from "../../services/reports";
import { useAuth } from "../../context/AuthContext";
import utils from "../../helpers/utils";
import { ReportOption, GenerateReportPayload } from "../../types";

interface ReportFormState {
  relatorio: string | null;
  start_date: string;
  end_date: string;
  professional_id?: number;
  action_id?: string;
}

interface ReportItem {
  additional_data: {
    validity?: string;
    date?: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

function Reports() {
  const { getUser } = useAuth();
  const [form, setForm] = useState<ReportFormState>({
    relatorio: null,
    start_date: "",
    end_date: "",
    professional_id: getUser()?.id,
  });
  const [load, setLoad] = useState(false);
  const [relatorios, setRelatorios] = useState<ReportOption[]>([]);
  const [data, setData] = useState<Record<string, ReactNode>[]>([]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setLoad(true);

    const payload: GenerateReportPayload = {
      start_date: form.start_date,
      end_date: form.end_date,
      action_id: form.action_id || form.relatorio || "",
      professional_id: getUser()?.id || 0,
    };

    const resp = (await reports.genReport(payload)) as ReportItem[];
    setData(
      resp.map((f) => {
        return {
          ...f.additional_data,
          validity: f.additional_data?.validity
            ? utils.formatDate(new Date(f.additional_data.validity as string))
            : null,
          date: f.additional_data?.date
            ? utils.formatDate(new Date(f.additional_data.date as string))
            : null,
        } as unknown as Record<string, ReactNode>;
      })
    );
    setLoad(false);
  };

  const loadReports = async () => {
    setRelatorios(await reports.getReports());
  };

  useEffect(() => {
    loadReports();
  }, []);

  const tableHead = data.length > 0 ? Object.keys(data[0]) : [];

  const exportToCSV = () => {
    if (data.length === 0) return;

    const headers = tableHead.join(",");
    const rows = data.map((row) =>
      tableHead.map((key) => `"${row[key] || ""}"`).join(",")
    );
    const csv = [headers, ...rows].join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `relatorio-${form.action_id}-${new Date().toISOString()}.csv`;
    a.click();
  };

  const translateHeader = (key: string) => {
    const translations: Record<string, string> = {
      name: "Nome",
      email: "E-mail",
      phone: "Telefone",
      date: "Data",
      validity: "Validade",
      status: "Status",
      type: "Tipo",
    };
    return (
      translations[key] ||
      key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " ")
    );
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 md:px-6 2xl:px-10">
      {/* Header */}
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-3xl font-semibold text-black dark:text-white">
          Relatórios
        </h2>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Formulário de Filtros */}
        <div className="lg:col-span-1">
          <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
            <div className="flex items-center gap-3 border-b border-stroke px-7 py-4 dark:border-strokedark">
              <FiFileText className="text-primary" size={20} />
              <h3 className="font-semibold text-black dark:text-white">
                Filtros
              </h3>
            </div>

            <form onSubmit={submit} className="p-7">
              <div className="mb-5">
                <label className="mb-3 block text-sm font-medium text-black dark:text-white">
                  Tipo de Relatório <span className="text-red-500">*</span>
                </label>
                <div className="relative z-20 bg-transparent dark:bg-form-input">
                  <select
                    name="action_id"
                    value={form.action_id || ""}
                    onChange={({ target }) =>
                      setForm({ ...form, [target.name]: target.value })
                    }
                    required
                    className="relative z-20 w-full appearance-none rounded border border-stroke bg-transparent px-5 py-3 outline-none transition focus:border-primary active:border-primary dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary"
                  >
                    <option value="" disabled>
                      Selecione um relatório
                    </option>
                    {relatorios.map((e) => (
                      <option key={e.id} value={e.id}>
                        {e.label}
                      </option>
                    ))}
                  </select>
                  <span className="absolute right-4 top-1/2 z-30 -translate-y-1/2">
                    <svg
                      className="fill-current"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <g opacity="0.8">
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M5.29289 8.29289C5.68342 7.90237 6.31658 7.90237 6.70711 8.29289L12 13.5858L17.2929 8.29289C17.6834 7.90237 18.3166 7.90237 18.7071 8.29289C19.0976 8.68342 19.0976 9.31658 18.7071 9.70711L12.7071 15.7071C12.3166 16.0976 11.6834 16.0976 11.2929 15.7071L5.29289 9.70711C4.90237 9.31658 4.90237 8.68342 5.29289 8.29289Z"
                          fill=""
                        />
                      </g>
                    </svg>
                  </span>
                </div>
              </div>

              <div className="mb-5">
                <label className="mb-3 block text-sm font-medium text-black dark:text-white">
                  Data de Início <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2">
                    <FiCalendar className="text-gray-500" size={20} />
                  </span>
                  <input
                    type="date"
                    name="start_date"
                    value={form.start_date}
                    onChange={({ target }) =>
                      setForm({ ...form, [target.name]: target.value })
                    }
                    required
                    className="w-full rounded border border-stroke bg-transparent py-3 pl-12 pr-4 font-medium outline-none transition focus:border-primary active:border-primary dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label className="mb-3 block text-sm font-medium text-black dark:text-white">
                  Data de Fim <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2">
                    <FiCalendar className="text-gray-500" size={20} />
                  </span>
                  <input
                    type="date"
                    name="end_date"
                    value={form.end_date}
                    onChange={({ target }) =>
                      setForm({ ...form, [target.name]: target.value })
                    }
                    required
                    className="w-full rounded border border-stroke bg-transparent py-3 pl-12 pr-4 font-medium outline-none transition focus:border-primary active:border-primary dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={load}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-purple-600 px-6 py-3 text-center font-medium text-white transition hover:bg-purple-700"
              >
                <FiSearch size={20} />
                {load ? "Gerando..." : "Gerar Relatório"}
              </button>
            </form>
          </div>
        </div>

        {/* Resultado */}
        <div className="lg:col-span-2">
          <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
            <div className="flex items-center justify-between border-b border-stroke px-7 py-4 dark:border-strokedark">
              <h3 className="font-semibold text-black dark:text-white">
                Resultado
              </h3>
              {data.length > 0 && (
                <button
                  onClick={exportToCSV}
                  className="inline-flex items-center gap-2 rounded border border-stroke px-4 py-2 text-sm font-medium text-black transition hover:bg-gray-100 dark:border-strokedark dark:text-white dark:hover:bg-meta-4"
                >
                  <FiDownload size={16} />
                  Exportar CSV
                </button>
              )}
            </div>

            <div className="p-7">
              {load ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="h-12 w-12 animate-spin rounded-full border-4 border-solid border-primary border-t-transparent"></div>
                  <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">
                    Gerando relatório...
                  </p>
                </div>
              ) : data.length > 0 ? (
                <div className="max-h-[600px] overflow-auto rounded-lg border border-stroke dark:border-strokedark">
                  <table className="w-full table-auto">
                    <thead className="sticky top-0 bg-gray-100 dark:bg-meta-4">
                      <tr>
                        {tableHead.map((head) => (
                          <th
                            key={head}
                            className="border-b border-stroke px-4 py-4 text-left text-sm font-semibold text-black dark:border-strokedark dark:text-white"
                          >
                            {translateHeader(head)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.map((row, index) => (
                        <tr
                          key={index}
                          className="border-b border-stroke transition hover:bg-gray-50 dark:border-strokedark dark:hover:bg-meta-4"
                        >
                          {tableHead.map((key) => (
                            <td
                              key={key}
                              className="px-4 py-4 text-sm text-black dark:text-white"
                            >
                              {row[key] || "-"}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="mb-4 rounded-full bg-gray-100 p-6 dark:bg-meta-4">
                    <FiFileText size={40} className="text-gray-400" />
                  </div>
                  <p className="text-lg font-medium text-gray-600 dark:text-gray-400">
                    Nenhum dado para exibir
                  </p>
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-500">
                    Selecione os filtros e clique em "Gerar Relatório"
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Reports;
